import {
  BankAccountInfiniteSelectField,
  InfiniteTable,
  InputDate,
} from '@/components/_common';
import DocumentTypesSelectField from '@/components/_common/DocumentTypesSelectField';
import { InstallmentFromGroupingsList } from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import formatterPrice from '@/utils/number/formatterPrice';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Input } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import moment from 'moment';
import React from 'react';
import { FaExchangeAlt } from 'react-icons/fa';

type Props = {
  installments: InstallmentFromGroupingsList[];
  setInstallments: React.Dispatch<
    React.SetStateAction<InstallmentFromGroupingsList[]>
  >;
};

const GenerateGroupingInstallmentList: React.FC<Props> = ({
  installments,
  setInstallments,
}) => {
  function setFieldValue([field, index]: [string, number], value: unknown) {
    setInstallments((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  }

  function setDocumentTypeId(documentTypeId: number) {
    setInstallments(installments.map((item) => ({ ...item, documentTypeId })));
  }

  function setBankAccountId(bankAccountId: number) {
    setInstallments(installments.map((item) => ({ ...item, bankAccountId })));
  }

  const columns: ColumnsType<InstallmentFromGroupingsList> = [
    {
      dataIndex: 'description',
      title: 'Descrição',
      width: COLUMNS_WIDTH.default,
      render: (value: string, _, i) => (
        <Input
          value={value}
          onChange={({ target }) =>
            setFieldValue(['description', i], target.value)
          }
        />
      ),
    },
    {
      dataIndex: 'documentNumber',
      title: 'Número do documento',
      width: COLUMNS_WIDTH.default,
      render: (value: string, _, i) => (
        <Input
          value={value}
          onChange={({ target }) =>
            setFieldValue(['documentNumber', i], target.value)
          }
        />
      ),
    },
    {
      dataIndex: 'documentTypeId',
      title: 'Tipo de documento',
      width: COLUMNS_WIDTH.default,
      filterDropdown: () => {
        return (
          <DocumentTypesSelectField
            style={{ width: 200, minWidth: COLUMNS_WIDTH.default }}
            onChange={setDocumentTypeId}
          />
        );
      },
      filterIcon: <FaExchangeAlt size={14} />,
      render: (value: string, _, i) => (
        <DocumentTypesSelectField
          value={value}
          onChange={(targetValue) =>
            setFieldValue(['documentTypeId', i], targetValue)
          }
          style={{
            border: installments.every(
              (installment) => !installment.documentTypeId,
            )
              ? '1px solid red'
              : undefined,
            width: '100%',
          }}
        />
      ),
    },
    {
      dataIndex: 'bankAccountId',
      title: 'Conta bancária',
      width: COLUMNS_WIDTH.default,
      filterDropdown: () => {
        return (
          <BankAccountInfiniteSelectField
            queryFilter={{ active: true }}
            allowClear
            style={{ width: 200, minWidth: COLUMNS_WIDTH.default }}
            onChange={setBankAccountId}
          />
        );
      },
      filterIcon: <FaExchangeAlt size={14} />,
      render: (value: string, _, i) => (
        <BankAccountInfiniteSelectField
          queryFilter={{ active: true }}
          allowClear
          value={value}
          onChange={(targetValue) =>
            setFieldValue(['bankAccountId', i], targetValue)
          }
          style={{
            border: installments.every(
              (installment) => !installment.bankAccountId,
            )
              ? '1px solid red'
              : undefined,
            width: '100%',
          }}
        />
      ),
    },
    {
      align: 'right',
      dataIndex: 'dueDate',
      title: 'Vencimento',
      width: COLUMNS_WIDTH.date,
      render: (value: string, _, i) => (
        <InputDate
          value={moment(value)}
          onChange={(targetValue) => setFieldValue(['dueDate', i], targetValue)}
        />
      ),
    },
    {
      align: 'right',
      dataIndex: 'installmentValue',
      title: 'Valor da parcela',
      width: COLUMNS_WIDTH.price,
      render: (value: number) => formatterPrice(value),
    },
  ];

  return (
    <InfiniteTable
      size="middle"
      loading={!installments}
      rowKey={(_row, index) => String(index)}
      dataSource={installments}
      columns={columns}
      scroll={{ x: getTableScrollX(columns) }}
    />
  );
};

export default GenerateGroupingInstallmentList;
